import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthFacade } from './auth.facade';
import { RouterFacade } from '../router/router.facade';
import { AvailableRouteWithoutAcceptingDPN } from './constants/auth-endpoint';
import { AuthUserData, AuthMeResponse, LoginData } from './models';
import { CountryCodes } from '../../../common';
import { AuthService } from './auth.service';
import { AuthState } from './auth.state';
import { Store } from '@ngxs/store';
import { AuthSetUser } from './auth.actions';
import { ProfileSetFromAuthMe } from '../administration';

@Injectable({
  providedIn: 'root',
})
export class AppAuthGuard  {
  constructor(
    private store: Store,
    private authFacade: AuthFacade,
    private routerFacade: RouterFacade,
    private authService: AuthService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canUserUseSystem(state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canUserUseSystem(state);
  }

  private canUserUseSystem(state: RouterStateSnapshot): Observable<boolean> {
    return this.store.select(AuthState.getUserData).pipe(
      take(1),
      switchMap((userDataFromState: AuthUserData | null) => {
        if (userDataFromState) {
          this.handleUserState(userDataFromState, state.url);
          return of(true);
        }

        return this.authService.me().pipe(
          tap((loginData: LoginData) => {
            this.store.dispatch([new AuthSetUser(loginData.response), new ProfileSetFromAuthMe(loginData.dto)]);
            this.handleUserState(loginData.response, state.url);
          }),
          map(() => true),
          catchError(() => {
            this.authFacade.logout(state.url);
            return of(false);
          }),
        );
      }),
    );
  }

  private handleUserState(userData: AuthMeResponse, url: string): void {
    if (
      !userData.isFirstLogin &&
      !userData.dpnAccepted &&
      userData.country === CountryCodes.CN &&
      this.isRouteClosed(url)
    ) {
      this.routerFacade.go({
        path: [AvailableRouteWithoutAcceptingDPN.DataProtectionNoticeAccept],
      });
    }

    if (userData.passwordExpired) {
      this.authFacade.redirectToChangePassword();
    }
  }

  private isRouteClosed(route: string): boolean {
    return !Object.values(AvailableRouteWithoutAcceptingDPN).includes(route as AvailableRouteWithoutAcceptingDPN);
  }
}
